import { ApiErrorCode, withApiErrorHandler } from '@/modules/common/api/errors';
import { PaginatedResponse } from '@/modules/common/types/pagination-types';
import { EntityValidationErrorResponse } from '@/modules/onboarding/common/types/entity-validation-error.response';
import { EntityValidationReportResponse } from '@/modules/onboarding/common/types/entity-validation-report.response';
import { GetEntitiesParams } from '@/modules/onboarding/common/types/get-entities.params';
import { DeleteEmployeesBulkRequest } from '@/modules/onboarding/team-setup/types/request/delete-employees-bulk.request';
import { MergeEmployeesRequest } from '@/modules/onboarding/team-setup/types/request/merge-employees.request';
import { UpdateEmployeesBulkRequest } from '@/modules/onboarding/team-setup/types/request/update-employee-bulk.request';
import { UpsertEmployeesBulkRequest } from '@/modules/onboarding/team-setup/types/request/upsert-employee-bulk.request';
import { CsvImportUsersErrorResponse } from '@/modules/onboarding/team-setup/types/response/csv-import-users-error.response';
import { axiosInstance } from '@/utils/axios';

import { mapEmployeeFromDTO } from '../mappers/employee.mapper';
import { Employee } from '../types/employees.types';

export interface DeleteEmployeesParams {
  employeeIds: number[];
}

export async function deleteEmployees(params: DeleteEmployeesParams): Promise<void> {
  const endpoint = '/api/opsium-users/bulk';
  const requestData: DeleteEmployeesBulkRequest = {
    ids: params.employeeIds,
  };

  await withApiErrorHandler(endpoint, ApiErrorCode.ONBOARDING_EMPLOYEES_DELETE_FAILED, () =>
    axiosInstance.delete(endpoint, { data: requestData })
  );
}

export async function getEmployees(
  params: GetEntitiesParams
): Promise<PaginatedResponse<Employee>> {
  const endpoint = '/api/opsium-users';

  const response = await withApiErrorHandler<PaginatedResponse<any>>(
    endpoint,
    ApiErrorCode.ONBOARDING_EMPLOYEES_LOAD_FAILED,
    () => axiosInstance.get(endpoint, { params })
  );

  return {
    ...response,
    items: response.items.map(mapEmployeeFromDTO),
  };
}

export async function upsertEmployeesBulk(params: UpsertEmployeesBulkRequest): Promise<void> {
  const endpoint = '/api/opsium-users/bulk';

  await withApiErrorHandler(endpoint, ApiErrorCode.ONBOARDING_EMPLOYEES_UPSERT_FAILED, () =>
    axiosInstance.post(endpoint, params)
  );
}

export async function updateEmployeesBulk(params: UpdateEmployeesBulkRequest): Promise<void> {
  const endpoint = '/api/opsium-users/bulk';

  await withApiErrorHandler(endpoint, ApiErrorCode.ONBOARDING_EMPLOYEES_UPDATE_FAILED, () =>
    axiosInstance.patch(endpoint, params)
  );
}

export async function getEmployeesValidationReport(): Promise<EntityValidationReportResponse> {
  const endpoint = '/api/opsium-users/validation-report';

  return withApiErrorHandler(
    endpoint,
    ApiErrorCode.ONBOARDING_EMPLOYEES_VALIDATION_REPORT_FAILED,
    () => axiosInstance.get(endpoint)
  );
}

export async function mergeEmployees(params: MergeEmployeesRequest): Promise<void> {
  const endpoint = '/api/opsium-users/merge';

  await withApiErrorHandler(endpoint, ApiErrorCode.ONBOARDING_EMPLOYEES_MERGE_FAILED, () =>
    axiosInstance.post(endpoint, params)
  );
}

export async function submitEmployees(): Promise<EntityValidationErrorResponse> {
  const endpoint = '/api/opsium-users/submit';

  return withApiErrorHandler(endpoint, ApiErrorCode.ONBOARDING_EMPLOYEES_SUBMIT_FAILED, () =>
    axiosInstance.post(endpoint)
  );
}

export async function importEmployeesFromCsv(file: File): Promise<CsvImportUsersErrorResponse> {
  const endpoint = '/api/opsium-users/import';
  const formData = new FormData();
  formData.append('file', file);

  return withApiErrorHandler(endpoint, ApiErrorCode.ONBOARDING_EMPLOYEES_IMPORT_FAILED, () =>
    axiosInstance.post(endpoint, formData, {
      headers: { 'Content-Type': 'multipart/form-data' },
    })
  );
}
